import { type FormikProps, useFormik } from "formik";
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { Icon } from "semantic-ui-react";
import { CopyableInput, FormSectionHeader } from "../../../components";
import ValidatedForm from "../../../components/forms/ValidatedFormWithoutFormik";
import ValidatedField from "components/forms/ValidatedField";
import { EndpointsToGetSasLink, RolePermissions } from "../../../enums";
import ImageAspectRatio from "../../../enums/imageAspectRatio";
import nameof from "../../../utils/nameof";
import { ensureTrimmed } from "utils/stringUtils";
import { getAccountSchema } from "utils/validationSchemas/accountValidationSchema";
import { type AccountConfigurationInfo, type AccountType, type ContractType, type ParentAccount } from "../types";
import Tooltip from "../../../components/common/tooltip/Tooltip";
import moment from "moment";
import _, { isEmpty } from "lodash";
import { TimezoneDropdown } from "features/Library/Common/Fields/timezone-dropdown/TimezoneDropdown";
import { WithFormikPayload } from "features/Library/Common/Fields/WithFormikPayload";
import { AccountInfoLayout } from "./AccountInfoLayout";
import { useHasPermissions } from "features/Application/hooks";
import { renderCustomLabel } from "components/multiSelect/helpers";
import type { FormBindingRef } from "hooks/useFormBinding";
import ImagePreview from "components/media/imagePreview/ImagePreview";
import {
  IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS,
  IMAGE_PREVIEW_PLACEHOLDER_VARIANTS,
} from "components/media/imagePreview/placeholders";
import styles from "./AccountConfiguration.module.scss";
import environmentConfig from "configuration/environmentConfig";

export interface AccountConfigurationProps {
  onIsValidChange: (isValid: boolean) => void;
  onDirtyChanged: (isDirty: boolean) => void;
  accountTypes: AccountType[];
  contractTypes: ContractType[];
  parentAccounts: ParentAccount[];
  isLoadingAccountTypes?: boolean;
  isLoadingContractTypes?: boolean;
  isLoadingParentAccounts?: boolean;
  isOwnAccount?: boolean;
  isEdit?: boolean;
  accountId?: number;
  onFetchParentAccounts?: (accountTypeId: number) => void;
  currentConfiguration?: AccountConfigurationInfo;
  isMobo: boolean;
  hasGlobalAccess: boolean;
  bindResetForm?: (resetForm: () => void) => void;
  bindSubmitForm?: (submitForm: () => void) => void;
  onSubmit: (info: AccountConfigurationInfo) => void;
  showAccountId?: boolean;
  prefilled?: {
    name: string;
    logoUri?: Nullable<string>;
  };
  onRemoveName?: () => void;
  onParentAccountChange?: (parentAccountId: number | null) => void;
  accountsLabels: string[];
  onAddNewTag: (tag: string) => void;
}

export const AccountConfiguration = forwardRef<FormBindingRef, AccountConfigurationProps>(
  (props: AccountConfigurationProps, ref) => {
    const {
      currentConfiguration,
      isMobo,
      hasGlobalAccess,
      parentAccounts,
      accountId,
      isEdit,
      onFetchParentAccounts,
      isOwnAccount,
      prefilled,
      onParentAccountChange,
      accountsLabels,
      onAddNewTag,
      contractTypes,
      accountTypes,
      showAccountId,
      bindResetForm,
      bindSubmitForm,
      isLoadingParentAccounts,
      isLoadingAccountTypes,
      isLoadingContractTypes,
      onIsValidChange,
      onDirtyChanged,
      onRemoveName,
    } = props;
    const hasCreatePermissions = useHasPermissions(RolePermissions.AccountsSettingsCreate);

    const isRequiredParent = !!isEdit && (isMobo || !hasGlobalAccess) && !isOwnAccount;
    const hideParent = !hasCreatePermissions && !isEdit;
    const showAccountLabels = !isOwnAccount;

    const accountTypeHasBeenChanged = useRef(false);

    const schema = useMemo(() => {
      return getAccountSchema(isRequiredParent, currentConfiguration?.name, currentConfiguration?.alias, isEdit);
    }, [isRequiredParent, currentConfiguration?.name, currentConfiguration?.alias, isEdit]);

    const defaultAccountConfiguration = useMemo(() => {
      return defaultAccount(
        hasCreatePermissions && !hasGlobalAccess ? accountId : null,
        prefilled?.name,
        prefilled?.logoUri,
      );
    }, [hasCreatePermissions, hasGlobalAccess, accountId, prefilled?.name, prefilled?.logoUri]);

    const readonlyTags = useMemo(() => {
      return currentConfiguration ? _.difference(currentConfiguration.readonlyTags, currentConfiguration.tags) : [];
    }, [currentConfiguration]);

    const initialValues = useMemo(() => {
      if (currentConfiguration?.accountTypeId) {
        return { ...currentConfiguration, tags: readonlyTags.concat(currentConfiguration.tags) };
      }
      return defaultAccountConfiguration;
    }, [currentConfiguration, defaultAccountConfiguration, readonlyTags]);

    const formik = useFormik<Partial<AccountConfigurationInfo>>({
      initialValues,
      onSubmit: (values: Partial<AccountConfigurationInfo>) => {
        const casted = schema.cast(values) as AccountConfigurationInfo;
        const result = {
          ...casted,
          tags: casted.tags.filter((tag) => !readonlyTags.includes(tag)),
        };

        props.onSubmit(result);
      },
      onReset: () => {
        accountTypeHasBeenChanged.current = false;
      },
      validationSchema: schema,
      validateOnMount: true,
      enableReinitialize: true,
    });

    const setTimeoutFieldValue = (name: string, value: number, shouldValidate?: boolean) => {
      formik.setTouched({
        sessionTimeoutDays: true,
        sessionTimeoutHours: true,
        sessionTimeoutMinutes: true,
        sessionTimeoutInMinutes: true,
      });

      return formik.setValues((prev) => {
        const result: Partial<AccountConfigurationInfo> = { ...prev, [name]: value };
        result.sessionTimeoutInMinutes = moment
          .duration({
            days: result.sessionTimeoutDays,
            hours: result.sessionTimeoutHours,
            minutes: result.sessionTimeoutMinutes,
          })
          .asMinutes();
        return result;
      }, shouldValidate);
    };

    const { dirty, setFieldValue, values, isValid, submitForm, resetForm } = formik;

    const {
      contractTypeId,
      contractTypeName,
      canChangeAccountType,
      canEdit,
      magicLinkLogin,
      tags,
      sessionTimeoutInMinutes,
      sessionTimeoutMinutes,
      sessionTimeoutHours,
      sessionTimeoutDays,
      isDemo,
      alias,
      timeZone,
      parentAccountId,
      parentName,
      accountTypeId,
    } = values;

    const parents = useMemo(() => {
      return getParents(parentAccounts);
    }, [parentAccounts]);

    const parentOptions = useMemo(() => {
      // handles the case when the current parent has lost permissions to the account to prevent parent to be silently removed
      // BE does't validate parent permissions on account update if parent has not been changed
      if (!!parentAccountId && !accountTypeHasBeenChanged.current && !parents.hash.has(parentAccountId)) {
        return [getOption({ id: parentAccountId, name: parentName! }), ...parents.options];
      }

      return parents.options;
    }, [parentAccountId, parentName, parents.hash, parents.options]);

    useEffect(() => {
      if (isOwnAccount || parents.hash.size === 0 || !accountTypeHasBeenChanged.current) return;
      if (!!parentAccountId && !parents.hash.has(parentAccountId)) {
        changeParentAccount(null);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parents.hash]);

    const accountsLabelsOptions: { text: string; value: string }[] = useMemo(() => {
      if (!currentConfiguration || accountsLabels.length === 0) {
        return [];
      }

      return accountsLabels
        .filter((label) => !readonlyTags.includes(label))
        .map((label) => ({ text: label, value: label }))
        .concat(readonlyTags.map((label) => ({ text: label, value: label, disabled: true })));
    }, [accountsLabels, currentConfiguration, readonlyTags]);

    const handleAddNewTag = (_: React.ChangeEvent<HTMLInputElement>, { value }: { value: string }) => {
      onAddNewTag(value);
    };

    const changeParentAccount = (value: number | null) => {
      setFieldValue("parentAccountId", value, true);
      onParentAccountChange?.(value);
    };

    const handleParentAccountChange = (_: unknown, data: { value: number }) => {
      changeParentAccount(data.value);
    };

    const handleAccountTypeChange = () => {
      accountTypeHasBeenChanged.current = true;
      if (!hasCreatePermissions || !props.accountId) return;

      const isParentAccountIdEmpty = !parentAccountId || parentAccountId === props.accountId;

      if (isParentAccountIdEmpty && !parents.hash.has(props.accountId)) {
        changeParentAccount(props.accountId);
      }
    };

    useEffect(() => {
      if (bindResetForm) {
        bindResetForm(resetForm);
      }
    }, [bindResetForm, resetForm]);

    useEffect(() => {
      if (bindSubmitForm) {
        bindSubmitForm(submitForm);
      }
    }, [bindSubmitForm, submitForm]);

    useEffect(() => {
      onDirtyChanged(dirty);
    }, [dirty, onDirtyChanged]);

    useEffect(() => {
      onIsValidChange(isValid);
    }, [isValid, onIsValidChange]);

    useEffect(() => {
      if (!accountTypeId || accountTypeId <= 0 || (isEdit && (!hasGlobalAccess || isOwnAccount))) {
        return;
      }
      onFetchParentAccounts?.(accountTypeId);
    }, [accountTypeId, hasGlobalAccess, isOwnAccount, isEdit, onFetchParentAccounts]);

    useImperativeHandle(ref, () => ({
      submitForm,
      resetForm,
    }));

    const getInfoForm = (isDisabled: boolean) => {
      const { name, logoUri, landscapeLogoUri } = formik.values;
      const image = (
        <ImagePreview
          value={logoUri}
          propertyName={nameof<AccountConfigurationInfo>("logoUri")}
          label={"Square Logo"}
          combinedIcon
          placeholder={IMAGE_PREVIEW_PLACEHOLDER_VARIANTS.account_image}
          headerPlaceholder={IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS.image}
          tooltip={{
            info: "Your square logo will be used throughout the platform",
            width: 16,
          }}
          endpointToGetSasLink={EndpointsToGetSasLink.Account}
          disabled={isEdit && !canEdit}
          isHidden
          aspectRatio={ImageAspectRatio.SQUARE_1X1}
          shouldValidate={false}
          showBestCrop
          editBtnEnabled={false}
          editable
          {...formik}
        />
      );

      const landscapeImage = (
        <ImagePreview
          value={landscapeLogoUri}
          propertyName={nameof<AccountConfigurationInfo>("landscapeLogoUri")}
          label="Landscape Logo"
          combinedIcon
          headerPlaceholder={IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS.image}
          placeholder={IMAGE_PREVIEW_PLACEHOLDER_VARIANTS.account_image}
          tooltip={{
            info: "Your landscape logo will be used primarily in email communications and the homepage for the end user portal",
            width: 18,
          }}
          endpointToGetSasLink={EndpointsToGetSasLink.Account}
          disabled={isEdit && !canEdit}
          isHidden
          aspectRatio={ImageAspectRatio.HD_3X1}
          shouldValidate={false}
          showBestCrop
          editBtnEnabled={false}
          editable
          {...formik}
        />
      );

      const trimName = () =>
        ensureTrimmed(name, (value) => setFieldValue(nameof<AccountConfigurationInfo>("name"), value));

      const handleDuplication = !isEdit;

      const inputProps = handleDuplication
        ? ({
            setFieldValue: (_: any, value: string) => {
              if (isEmpty(value)) {
                onRemoveName?.();
              }
            },
          } as FormikProps<Partial<AccountConfigurationInfo>>)
        : formik;
      return (
        <AccountInfoLayout
          image={image}
          landscapeImage={landscapeImage}
          name={
            <ValidatedForm.InputField
              label="Company Name"
              value={name}
              propertyName={nameof<AccountConfigurationInfo>("name")}
              placeholder="Enter Company Name"
              markAsRequired
              disabled={isDisabled}
              clearable={handleDuplication}
              onBlur={trimName}
              {...inputProps}
            />
          }
        />
      );
    };

    const isNonExistentContractType = !contractTypes.some((contract) => contract.id === contractTypeId);

    const isDisabled = !!isEdit && (!canEdit || !!isOwnAccount);

    const accountTypesOptions = getOptions(accountTypes);

    const handleLabelsChange = (_: string, value: { selected: string[] }) => {
      formik.setFieldValue("tags", value.selected);
    };

    const trimAlias = () => {
      ensureTrimmed(alias, (value) => setFieldValue(nameof<AccountConfigurationInfo>("alias"), value));
    };

    return (
      <section className="account-info-form">
        <ValidatedForm
          loaderOnSubmit={!isEdit}
          unsavedChangesPrompt={{
            title: "Exit Without Saving?",
            message: "Are you sure you want to exit without saving this account? All information entered will be lost.",
          }}
          {...formik}
        >
          <>
            <section className={styles.section}>
              <FormSectionHeader title="Account Information" />
              {showAccountId && (
                <ValidatedField propertyName="" label="Account ID">
                  <CopyableInput value={accountId!.toString()} readonly />
                </ValidatedField>
              )}
              {getInfoForm(isDisabled)}
              <ValidatedForm.InputField
                label="Account Alias"
                value={alias}
                propertyName={nameof<AccountConfigurationInfo>("alias")}
                placeholder={isEdit ? undefined : "e.g. company"}
                disabled={isDisabled}
                onBlur={trimAlias}
                {...formik}
              />

              <ValidatedForm.DropdownField
                placeholder="Select Account Type"
                label="Account Type"
                value={accountTypeId}
                propertyName={nameof<AccountConfigurationInfo>("accountTypeId")}
                className={styles["account-type"]}
                options={accountTypesOptions}
                isLoading={isLoadingAccountTypes}
                disabled={isLoadingAccountTypes || (isEdit && (!canChangeAccountType || !canEdit || isOwnAccount))}
                markAsRequired
                onChangeCallback={handleAccountTypeChange}
                {...formik}
              >
                {hasGlobalAccess && (
                  <div className={styles.demo}>
                    <ValidatedForm.CheckboxField
                      toggle
                      propertyName={nameof<AccountConfigurationInfo>("isDemo")}
                      value={isDemo}
                      fieldClassName={styles.demo__toggle}
                      disabled={isDisabled}
                      {...formik}
                    />
                    Demo
                  </div>
                )}
              </ValidatedForm.DropdownField>

              {!hideParent && (
                <ValidatedForm.DropdownField
                  placeholder="Select Parent Account"
                  label="Parent Account"
                  value={parentAccountId}
                  minCharacters={3}
                  propertyName={nameof<AccountConfigurationInfo>("parentAccountId")}
                  options={parentOptions}
                  isLoading={isLoadingParentAccounts}
                  disabled={isLoadingParentAccounts || !accountTypeId || (isEdit && (!hasGlobalAccess || isOwnAccount))}
                  search
                  clearable
                  markAsRequired={isRequiredParent}
                  onChangeCallback={handleParentAccountChange}
                  {...formik}
                />
              )}
              {showAccountLabels && (
                <ValidatedForm.MultiSelectWithAdditionField
                  {...formik}
                  options={accountsLabelsOptions}
                  label="Labels"
                  value={tags}
                  propertyName={nameof<AccountConfigurationInfo>("tags")}
                  onAddItem={handleAddNewTag}
                  disabled={isDisabled}
                  shouldValidate
                  isCaseSensitive
                  setFieldValue={handleLabelsChange}
                  renderLabel={renderCustomLabel}
                  placeholder={"Type to add label"}
                  readonly={readonlyTags.length === tags?.length}
                />
              )}
            </section>

            <section className={styles.section}>
              <FormSectionHeader title="Contract Information" />
              <ValidatedForm.DropdownField
                text={isNonExistentContractType ? contractTypeName : ""}
                placeholder="Select One"
                label="Contract Type"
                value={contractTypeId}
                propertyName={nameof<AccountConfigurationInfo>("contractTypeId")}
                options={getOptions(contractTypes)}
                isLoading={isLoadingContractTypes}
                disabled={isLoadingContractTypes || isDisabled}
                markAsRequired
                {...formik}
              />
            </section>

            <section className={styles.section}>
              <FormSectionHeader title="Authentication Settings" />
              <div className={styles["magic-login"]}>
                <ValidatedForm.CheckboxField
                  toggle
                  propertyName={nameof<AccountConfigurationInfo>("magicLinkLogin")}
                  value={magicLinkLogin}
                  fieldClassName={styles["magic-login__toggle"]}
                  disabled={isEdit && !canEdit}
                  {...formik}
                />
                <label
                  htmlFor={nameof<AccountConfigurationInfo>("magicLinkLogin")}
                  className={styles["magic-login__label"]}
                >
                  Enable Magic Link Authentication
                </label>
                <Tooltip
                  tooltipClassName={styles["magic-login__tooltip"]}
                  className={styles["magic-login__tooltip-target"]}
                  target={<Icon className="info circle" />}
                  content={magicLoginTooltip}
                  position="top center"
                  hoverable
                  hideOnScroll
                  showAlways
                />
              </div>
            </section>

            {isEdit && (
              <section className={styles.section}>
                <div className={styles["section-header"]}>
                  <FormSectionHeader title="Session Timeout" />
                  <Tooltip
                    tooltipClassName={styles["section-header__tooltip"]}
                    className={styles["section-header__tooltip-target"]}
                    target={<Icon className="info circle" />}
                    content={sessionTimeoutTooltip}
                    position="top center"
                    hoverable
                    hideOnScroll
                    showAlways
                  />
                </div>
                <div className={styles["session-timeout"]}>
                  <div className={styles["timeout-input"]}>
                    <ValidatedForm.InputField
                      label="Days"
                      value={sessionTimeoutDays}
                      propertyName={nameof<AccountConfigurationInfo>("sessionTimeoutDays")}
                      disabled={!canEdit}
                      errorPosition={"top"}
                      inputType={"number"}
                      {...formik}
                      setFieldValue={setTimeoutFieldValue}
                    />
                  </div>
                  <div className={styles["timeout-input"]}>
                    <ValidatedForm.InputField
                      label="Hours"
                      value={sessionTimeoutHours}
                      propertyName={nameof<AccountConfigurationInfo>("sessionTimeoutHours")}
                      disabled={!canEdit}
                      errorPosition={"top"}
                      inputType={"number"}
                      {...formik}
                      setFieldValue={setTimeoutFieldValue}
                    />
                  </div>
                  <div className={styles["timeout-input"]}>
                    <ValidatedForm.InputField
                      label="Minutes"
                      value={sessionTimeoutMinutes}
                      propertyName={nameof<AccountConfigurationInfo>("sessionTimeoutMinutes")}
                      disabled={!canEdit}
                      errorPosition={"top"}
                      inputType={"number"}
                      {...formik}
                      setFieldValue={setTimeoutFieldValue}
                    />
                  </div>
                  <div className={styles["timeout-input"]}>
                    <ValidatedForm.InputField
                      className={styles["total-minutes"]}
                      value={sessionTimeoutInMinutes}
                      propertyName={nameof<AccountConfigurationInfo>("sessionTimeoutInMinutes")}
                      disabled={!canEdit}
                      {...formik}
                    />
                  </div>
                </div>
              </section>
            )}

            <section className={styles.section}>
              <div className={styles["section-header"]}>
                <FormSectionHeader title="DEFAULT TIMEZONE" />
                <Tooltip
                  tooltipClassName={styles["section-header__tooltip"]}
                  className={styles["section-header__tooltip-target"]}
                  target={<Icon className="info circle" />}
                  content={
                    "Sets the default timezone for this account. End-users can override it in their individual settings."
                  }
                  position="top center"
                  hoverable
                  hideOnScroll
                  showAlways
                />
              </div>
              <WithFormikPayload
                propertyName={nameof<AccountConfigurationInfo>("timeZone")}
                {...formik}
                render={(props) => (
                  <TimezoneDropdown
                    label="Timezone"
                    placeholder="Default is UTC. Select to customize for this account."
                    setFieldTouched={formik.setFieldTouched}
                    setFieldValue={formik.setFieldValue}
                    value={timeZone}
                    clearable
                    disabled={isEdit && !canEdit}
                    {...props}
                  />
                )}
              />
            </section>
          </>
        </ValidatedForm>
      </section>
    );
  },
);

const defaultAccount = (
  parentAccountId?: number | null,
  name?: string,
  logoUri?: Nullable<string>,
): Partial<AccountConfigurationInfo> => {
  return {
    name: name || "",
    isDemo: false,
    magicLinkLogin: true,
    parentAccountId,
    logoUri,
    tags: [],
  };
};

const getOptions = (options: { id: number; name: string }[]) => {
  return options.map(getOption);
};

const getOption = (option: { id: number; name: string }) => ({
  key: option.id,
  value: option.id,
  text: option.name,
});

const getParents = (parentAccounts: ParentAccount[]) => {
  const hash = new Set<number>();
  const options: ReturnType<typeof getOptions> = [];
  parentAccounts.forEach((account) => {
    hash.add(account.id);
    options.push(getOption(account));
  });
  return {
    hash,
    options,
  };
};

const helpCenterLink = environmentConfig.helpCenterUrl;
const helpCenterRelativeUrls = environmentConfig.helpCenterRelativeUrls;
const supportLink = `${helpCenterLink}/${helpCenterRelativeUrls.magicLinkUrl}`;

const magicLoginTooltip = (
  <span>
    Activate this feature to offer users a login link via email for a simplified login process.&nbsp;
    <a href={supportLink} target="_blank" rel="noopener noreferrer">
      Visit our Help Center
    </a>
    &nbsp;for more information.
  </span>
);

const sessionTimeoutTooltip = <span>Adjust the session timeout duration. Minimum: 10 minutes. Maximum: 365 days.</span>;
